import {
	BOOKABLE_STATUSES,
	CONTINUITY_MODE,
	type ContinuityMode,
	DEFAULT_MAX_VISIT_WEEKS,
} from "./constants";
import { toIsoDate } from "./date";
import type { TherapistCalendarDay } from "./types";

export type VisitTherapist = { id: string };
/**
 * Structural placeholder for a scheduled visit. Replaced by the repo's real
 * series-visit type in Phase D (see plan Task D-5).
 */
export type VisitSchedule = {
	therapist: VisitTherapist | null;
	date: string | null;
};

/** A visit only counts as an anchor when it has BOTH a date and a therapist. */
export function isVisitScheduled(v: VisitSchedule | undefined | null): boolean {
	return !!v && !!v.therapist && !!v.date;
}

/**
 * Continuity anchor for the visit being edited = the SERIES LEAD (visit 1)
 * therapist, always ("pure visit 1"). Returns null for visit 1 itself (index 0),
 * for CONTINUITY_MODE.OFF, or when visit 1 is not yet scheduled (→ show-all).
 *
 * The edited visit's OWN therapist and the PREVIOUS visit's therapist are
 * intentionally NOT considered — every later visit is pulled back toward whoever
 * started the series, matching single & bulk reschedule.
 */
export function resolveContinuityTherapist(params: {
	mode: ContinuityMode;
	visits: VisitSchedule[];
	index: number;
}): VisitTherapist | null {
	const { mode, visits, index } = params;
	if (mode === CONTINUITY_MODE.OFF || index === 0) {
		return null;
	}

	return isVisitScheduled(visits[0]) ? visits[0].therapist : null;
}

/** §4.2 range: start = min selectable date, end = min + maxWeeks. */
export function getCalendarDateRange(
	minDate: Date,
	maxWeeks: number = DEFAULT_MAX_VISIT_WEEKS,
): { start_date: string; end_date: string } {
	const end = new Date(minDate);
	end.setDate(end.getDate() + maxWeeks * 7);
	return { start_date: toIsoDate(minDate), end_date: toIsoDate(end) };
}

/**
 * §4.2 coloring: available = AVAILABLE/PARTIALLY_BOOKED; unavailable =
 * NOT_WORKING/FULLY_BOOKED. A date with NO entry (past, or beyond the fetched
 * window) is UNKNOWN — neither available nor unavailable: it stays uncoloured and
 * must NOT trigger the show-all fallback, since "no data" isn't "therapist busy".
 * Unavailable dates stay clickable — the caller must NOT disable them.
 */
export function getCalendarModifiers(calendar: TherapistCalendarDay[]): {
	available: (d: Date) => boolean;
	unavailable: (d: Date) => boolean;
} {
	const byDate = new Map(calendar.map((c) => [c.date, c.availability_status]));
	const positive = new Set<string>(BOOKABLE_STATUSES);
	const statusFor = (d: Date) => byDate.get(toIsoDate(d));
	return {
		available: (d) => positive.has(statusFor(d) ?? ""),
		unavailable: (d) => {
			const s = statusFor(d);
			return s !== undefined && !positive.has(s);
		},
	};
}
